import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Car, CreditCard, ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { useLang } from "@/lib/i18n";
import { api } from "@/lib/api";

const Phone = ({ children, delay = 0, float = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 60 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    <motion.div
      animate={{ y: [0, -12, 0] }}
      transition={{ duration: 5 + float, repeat: Infinity, ease: "easeInOut" }}
      className="relative w-[220px] sm:w-[250px] rounded-[2.4rem] border border-white/15 bg-[#0A1220] p-2 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.8)]"
    >
      <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-5 bg-[#0A1220] rounded-b-xl z-10" />
      <div className="rounded-[1.9rem] bg-[#060B14] overflow-hidden aspect-[9/19] flex flex-col">
        {children}
      </div>
    </motion.div>
  </motion.div>
);

const InfoCard = ({ label, value }) => (
  <div className="border border-white/10 p-5 bg-ink/60">
    <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/40">{label}</span>
    <p className="mt-2 font-serif text-xl text-ivory">{value}</p>
  </div>
);

export default function CurrentFocus() {
  const { t } = useLang();
  const [nubai, setNubai] = useState(null);

  useEffect(() => {
    api.get("/projects")
      .then((r) => setNubai(r.data.find((p) => p.name === "Nu Bai") || null))
      .catch(() => {});
  }, []);

  const goResources = () => document.querySelector("#resources")?.scrollIntoView({ behavior: "smooth" });
  const requestInfo = () => window.dispatchEvent(new CustomEvent("bsg:contact-interest", { detail: "Nu Bai" }));

  return (
    <section id="focus" data-testid="focus-section" className="relative py-28 lg:py-40 bg-surface/40 border-y border-white/10 overflow-hidden">
      <div className="absolute top-1/3 right-0 w-[600px] h-[600px] bg-gold/[0.05] blur-[140px] rounded-full pointer-events-none" />
      <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-forest/15 blur-[120px] rounded-full pointer-events-none" />

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-16 items-center relative">
        <div className="lg:col-span-6">
          <Reveal className="flex items-center gap-4 mb-8">
            <span className="font-mono text-[11px] tracking-[0.3em] text-gold">07</span>
            <span className="h-px w-10 bg-gold/60" />
            <span data-testid="focus-label" className="flex items-center gap-2 font-mono text-[11px] tracking-[0.3em] uppercase text-white/50">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              {t("focus.label")}
            </span>
          </Reveal>

          <Reveal>
            <h2 className="font-serif text-6xl sm:text-7xl lg:text-8xl tracking-tight text-ivory leading-none" data-testid="focus-title">
              NU <span className="italic text-gold-light">BAI</span>
            </h2>
            <p className="mt-6 font-serif text-2xl sm:text-3xl text-white/80 leading-snug">
              {t("focus.tagline.a")} <span className="italic text-gold-light">{t("focus.tagline.b")}</span>
            </p>
          </Reveal>

          <Reveal delay={0.15} className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg">
            <div className="flex items-start gap-3">
              <Car size={16} className="text-gold mt-1 shrink-0" />
              <div>
                <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-ivory">{t("focus.mobility")}</p>
                <p className="text-sm text-white/50 mt-1">{t("focus.mobility.d")}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CreditCard size={16} className="text-gold mt-1 shrink-0" />
              <div>
                <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-ivory">{t("focus.pay")}</p>
                <p className="text-sm text-white/50 mt-1">{t("focus.pay.d")}</p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.2} className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <InfoCard label={t("focus.market")} value="Cabo Verde" />
            <InfoCard label={t("focus.stage")} value={t("focus.stage.v")} />
            <InfoCard label={t("focus.scope")} value={t("focus.scope.v")} />
            <InfoCard label={t("focus.objective")} value={t("focus.objective.v")} />
          </Reveal>

          <Reveal delay={0.25}>
            <p className="mt-10 text-white/60 leading-relaxed max-w-xl text-base md:text-lg">{t("focus.p")}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <button
                data-testid="focus-phase1-btn"
                onClick={goResources}
                className="group inline-flex items-center gap-2 px-8 py-4 bg-gold text-ink font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-gold-light transition-colors duration-300"
              >
                {t("focus.cta1")} <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </button>
              <button
                data-testid="focus-request-info-btn"
                onClick={requestInfo}
                className="inline-flex items-center gap-2 px-8 py-4 border border-white/20 text-ivory font-mono text-[11px] tracking-[0.2em] uppercase hover:border-gold hover:text-gold transition-colors duration-300"
              >
                {t("focus.cta2")} <ArrowUpRight size={14} />
              </button>
              {nubai?.page_enabled && nubai?.page_url && (
                <a
                  data-testid="focus-explore-btn"
                  href={nubai.page_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-8 py-4 border border-gold/50 text-gold font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-gold/10 transition-colors duration-300"
                >
                  {t("focus.explore")} <ArrowUpRight size={14} />
                </a>
              )}
            </div>
            <p data-testid="focus-disclosure" className="mt-10 font-mono text-[10px] leading-relaxed tracking-wide text-white/30 max-w-xl">
              {t("focus.disclosure")}
            </p>
          </Reveal>
        </div>

        <div className="lg:col-span-6 flex items-end justify-center gap-6 lg:gap-10">
          <Phone delay={0.2}>
            <div className="px-5 pt-10 pb-4 flex-1 flex flex-col">
              <span className="font-serif italic text-lg text-gold-light">nu bai</span>
              <div className="mt-4 flex-1 rounded-xl border border-white/10 bg-[#0B1526] relative overflow-hidden">
                <svg className="absolute inset-0 w-full h-full opacity-40" viewBox="0 0 100 160" preserveAspectRatio="none">
                  <path d="M10 0 L20 160 M50 0 L45 160 M85 0 L75 160 M0 40 L100 35 M0 90 L100 95 M0 130 L100 125" stroke="#D4AF37" strokeWidth="0.3" fill="none" />
                </svg>
                <div className="absolute top-6 left-1/2 -translate-x-1/2 w-2.5 h-2.5 rounded-full bg-gold shadow-[0_0_12px_#D4AF37]" />
                <div className="absolute bottom-8 left-8 w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_10px_#2D6A4F]" />
              </div>
              <div className="mt-3 rounded-xl border border-white/10 bg-white/[0.03] p-3">
                <p className="font-mono text-[8px] tracking-[0.2em] uppercase text-white/40">{t("focus.mobility")}</p>
                <div className="mt-2 h-8 rounded-lg bg-gold flex items-center justify-center">
                  <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-ink font-medium">Nu Bai</span>
                </div>
              </div>
            </div>
          </Phone>
          <Phone delay={0.4} float={1.5}>
            <div className="px-5 pt-10 pb-4 flex-1 flex flex-col">
              <span className="font-serif italic text-lg text-gold-light">nu bai <span className="not-italic font-mono text-[10px] tracking-[0.2em] text-white/50">PAY</span></span>
              <div className="mt-4 rounded-xl bg-gradient-to-br from-[#12203A] to-[#0A1220] border border-gold/30 p-4 aspect-[8/5]">
                <div className="w-7 h-5 rounded bg-gold/70" />
                <p className="mt-4 font-mono text-[10px] tracking-[0.3em] text-white/70">•••• 4587</p>
                <p className="mt-1 font-mono text-[7px] tracking-[0.2em] uppercase text-white/35">Nu Bai Pay</p>
              </div>
              <div className="mt-3 space-y-2">
                {["Praia City", "Market", "Top-up"].map((x) => (
                  <div key={x} className="flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2">
                    <span className="font-mono text-[8px] tracking-wide text-white/50">{x}</span>
                    <span className="font-mono text-[8px] text-gold">—</span>
                  </div>
                ))}
              </div>
            </div>
          </Phone>
        </div>
      </div>
    </section>
  );
}
